import React, { useState, useEffect, useRef } from 'react';
import { useListingApi } from '../../hooks/useListingApi';
import { useListingTypes } from '../../hooks/useListingTypeApi';
import { ListingDTO } from '../../services/apiClient';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { MAP_BOX_KEY } from '../../config/config';
import MapboxAutocomplete from '../../components/common/formElements/MapboxAutocomplete';
import Button from '../../components/common/Button';
import { Search, ZoomIn, ZoomOut, Locate } from 'lucide-react';
import Drawer from '../../components/common/Drawer';
import ListingForm from '../../components/forms/ListingForm';
import { ListingTypeConstants } from '../../constants/listingTypeConstants';

const ListingSearch: React.FC = () => {
  const { searchByProximity, isLoading } = useListingApi();
  const { data: listingTypesData = [], isLoading: isLoadingTypes } = useListingTypes();
  const [listings, setListings] = useState<ListingDTO[]>([]);
  const [selectedListing, setSelectedListing] = useState<ListingDTO | null>(null);
  const [searchName, setSearchName] = useState('');
  const [searchAddress, setSearchAddress] = useState('');
  const [searchCoords, setSearchCoords] = useState<{ latitude: number; longitude: number } | null>(null);
  const [listingTypeId, setListingTypeId] = useState<string>(ListingTypeConstants.SHOOTING_RANGE);
  const [listingTypeOptions, setListingTypeOptions] = useState<{ value: string; label: string }[]>([]);
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);
  const [hasMapMoved, setHasMapMoved] = useState(false);
  const [isProfileDrawerOpen, setIsProfileDrawerOpen] = useState(false);
  const [selectedListingForProfile, setSelectedListingForProfile] = useState<ListingDTO | null>(null);
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const markersRef = useRef<mapboxgl.Marker[]>([]);
  const lastSearchCenterRef = useRef<{ lat: number; lng: number } | null>(null);

  useEffect(() => {
    setListingTypeOptions(
      listingTypesData.map(type => ({
        value: type.id || '',
        label: type.title || ''
      }))
    );
  }, [listingTypesData]);

  useEffect(() => {
    loadListings();
  }, []);

  useEffect(() => {
    if (!mapContainerRef.current) return;
    if (mapRef.current) return; // Already initialized

    // Initialize map
    mapboxgl.accessToken = MAP_BOX_KEY || '';
    
    const map = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: 'mapbox://styles/mapbox/streets-v12',
      center: [22.9375, -30.5595], // South Africa center
      zoom: 5
    });

    mapRef.current = map;

    // Ensure map resizes to container after load
    map.on('load', () => {
      map.resize();
    });

    // Track when map is moved/panned by user
    map.on('moveend', () => {
      if (lastSearchCenterRef.current && mapRef.current) {
        const currentCenter = mapRef.current.getCenter();
        const distance = Math.sqrt(
          Math.pow(currentCenter.lat - lastSearchCenterRef.current.lat, 2) +
          Math.pow(currentCenter.lng - lastSearchCenterRef.current.lng, 2)
        );
        // If moved more than ~0.1 degrees (~10km), show notification
        setHasMapMoved(distance > 0.1);
      }
    });

    return () => {
      markersRef.current.forEach(marker => marker.remove());
      markersRef.current = [];
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (mapRef.current) {
      updateMapMarkers();
    }
  }, [listings]);

  const loadListings = async (lat?: number, lng?: number, name?: string, typeId?: string) => {
    try {
      const selectedTypeId = typeId !== undefined ? typeId : listingTypeId;
      
      // If coordinates provided (from search), use them
      if (lat !== undefined && lng !== undefined) {
        // Save search center for tracking
        lastSearchCenterRef.current = { lat, lng };
        setHasMapMoved(false);
        
        const result = await searchByProximity(lat, lng, 30, name, selectedTypeId);
        if (result?.data) {
          setListings(result.data as any);
          // If we have results and it was a name search, snap to first result
          if (name && result.data.length > 0 && mapRef.current) {
            const firstResult = result.data[0] as any;
            mapRef.current.flyTo({
              center: [Number(firstResult.longitude), Number(firstResult.latitude)],
              zoom: 12,
              essential: true
            });
          }
        }
        return;
      }

      // Try to get user's location
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          async (position) => {
            lastSearchCenterRef.current = { 
              lat: position.coords.latitude, 
              lng: position.coords.longitude 
            };
            setHasMapMoved(false);
            
            const result = await searchByProximity(
              position.coords.latitude,
              position.coords.longitude,
              30,
              name,
              selectedTypeId
            );
            if (result?.data) {
              setListings(result.data as any);
            }
          },
          async () => {
            // Fall back to default location if user denies
            lastSearchCenterRef.current = { lat: -30.5595, lng: 22.9375 };
            setHasMapMoved(false);
            
            const result = await searchByProximity(undefined, undefined, 30, name, selectedTypeId);
            if (result?.data) {
              setListings(result.data as any);
            }
          }
        );
      } else {
        // Browser doesn't support geolocation
        lastSearchCenterRef.current = { lat: -30.5595, lng: 22.9375 };
        setHasMapMoved(false);
        
        const result = await searchByProximity(undefined, undefined, 30, name, selectedTypeId);
        if (result?.data) {
          setListings(result.data as any);
        }
      }
    } catch (error) {
      console.error('Error loading listings:', error);
    }
  };

  const handleSearch = () => {
    if (searchCoords) {
      // Search by location coordinates (hits backend API)
      loadListings(searchCoords.latitude, searchCoords.longitude, searchName.trim() || undefined);
      if (mapRef.current) {
        mapRef.current.flyTo({
          center: [searchCoords.longitude, searchCoords.latitude],
          zoom: 10,
          essential: true
        });
      }
    } else if (searchName.trim()) {
      // If only name is provided, search backend by name (ignores proximity)
      if (mapRef.current) {
        const center = mapRef.current.getCenter();
        loadListings(center.lat, center.lng, searchName.trim());
      } else {
        // Use default location with name search
        loadListings(-30.5595, 22.9375, searchName.trim());
      }
    }
  };

  const handleClearSearch = () => {
    setSearchName('');
    setSearchAddress('');
    setSearchCoords(null);
    setListingTypeId(ListingTypeConstants.SHOOTING_RANGE);
    loadListings(undefined, undefined, undefined, ListingTypeConstants.SHOOTING_RANGE);
  };

  const handleLocationSelect = (latitude: number, longitude: number) => {
    // Set coordinates for search
    setSearchCoords({ latitude, longitude });
    
    // Fly map to selected location
    if (mapRef.current) {
      mapRef.current.flyTo({
        center: [longitude, latitude],
        zoom: 12,
        essential: true
      });
    }
    
    // Automatically search this location
    loadListings(latitude, longitude, searchName.trim() || undefined);
  };

  // Client-side filtering is no longer needed since backend handles name search
  // Keep this for backwards compatibility but return all listings
  const filterListingsByName = (allListings: ListingDTO[]) => {
    return allListings;
  };

  const updateMapMarkers = () => {
    if (!mapRef.current) return;

    // Clear existing markers
    markersRef.current.forEach(marker => marker.remove());
    markersRef.current = [];

    // Add new markers
    listings.forEach(listing => {
      const marker = new mapboxgl.Marker({ color: '#f1891d' })
        .setLngLat([Number(listing.longitude), Number(listing.latitude)])
        .addTo(mapRef.current!);

      marker.getElement().addEventListener('click', () => {
        setSelectedListing(listing);
      });

      markersRef.current.push(marker);
    });

    // Fit map to markers if we have listings
    if (listings.length > 0) {
      const bounds = new mapboxgl.LngLatBounds();
      listings.forEach(listing => {
        bounds.extend([Number(listing.longitude), Number(listing.latitude)]);
      });
      mapRef.current.fitBounds(bounds, { padding: 50, maxZoom: 12 });
    }
  };

  const handleListingClick = (listing: ListingDTO) => {
    setSelectedListing(listing);
    if (mapRef.current) {
      mapRef.current.flyTo({
        center: [Number(listing.longitude), Number(listing.latitude)],
        zoom: 14,
        essential: true
      });
    }
  };

  const handleZoomIn = () => {
    if (mapRef.current) {
      mapRef.current.zoomIn();
    }
  };

  const handleZoomOut = () => {
    if (mapRef.current) {
      mapRef.current.zoomOut();
    }
  };

  const handleGoToMyLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          if (mapRef.current) {
            mapRef.current.flyTo({
              center: [position.coords.longitude, position.coords.latitude],
              zoom: 10,
              essential: true
            });
          }
        },
        (error) => {
          console.error('Error getting location:', error);
          alert('Unable to access your location. Please enable location services.');
        }
      );
    } else {
      alert('Geolocation is not supported by your browser.');
    }
  };

  const handleSearchThisArea = () => {
    if (mapRef.current) {
      const center = mapRef.current.getCenter();
      lastSearchCenterRef.current = { lat: center.lat, lng: center.lng };
      setHasMapMoved(false);
      loadListings(center.lat, center.lng, searchName.trim() || undefined, listingTypeId);
    }
  };

  return (
    <div className="flex flex-col h-screen">
      {/* Top bar - Logo and Submit */}
      <div className="bg-white border-b border-gray-200 shadow-sm">
        <div className="px-8 py-4">
          <div className="flex items-center justify-between">
            <img
              src="/images/logo-linnear-navy.png"
              alt="Natshoot Logo"
              className="h-12"
            />
            <Button
              onClick={() => window.open('/register-listing', '_blank')}
              variant="primary"
              className="!py-2 !px-6"
            >
              Submit Listing
            </Button>
          </div>
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* Left sidebar - Search and Listings list */}
        <div className="w-1/3 overflow-y-auto bg-gray-50 border-r border-gray-200">
          {/* Collapsible Search Panel */}
          <div className="bg-white border-b border-gray-200">
            <button
              onClick={() => setIsSearchExpanded(!isSearchExpanded)}
              className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors"
            >
              <span className="font-semibold text-gray-800">Search Filters</span>
              <svg
                className={`w-5 h-5 text-gray-600 transition-transform ${isSearchExpanded ? 'rotate-180' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            
            {isSearchExpanded && (
              <div className="px-4 py-4 space-y-4 border-t border-gray-100">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Search by Name
                  </label>
                  <input
                    type="text"
                    value={searchName}
                    onChange={(e) => setSearchName(e.target.value)}
                    placeholder="Enter range name or city..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Search by Location
                  </label>
                  <MapboxAutocomplete
                    fieldId="searchAddress"
                    fieldLabel=""
                    value={searchAddress}
                    onChange={(e) => setSearchAddress(e.target.value)}
                    onLocationSelect={handleLocationSelect}
                    placeholder="Enter address or city..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Listing Type
                  </label>
                  <select
                    value={listingTypeId}
                    onChange={(e) => setListingTypeId(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                    disabled={isLoadingTypes}
                  >
                    {listingTypeOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div className="flex gap-2 pt-2">
                  <Button
                    onClick={handleSearch}
                    variant="primary"
                    disabled={!searchCoords && !searchName}
                    className="flex-1"
                  >
                    Search
                  </Button>
                  <Button
                    onClick={handleClearSearch}
                    variant="secondary"
                    className="flex-1"
                  >
                    Clear
                  </Button>
                </div>
              </div>
            )}
          </div>

          <div className="p-4">
            <h1 className="text-2xl font-bold text-gray-800 mb-4">
              Shooting Ranges
            </h1>

            {isLoading ? (
              <div className="flex justify-center items-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
            ) : (
              <div className="space-y-4">
                {filterListingsByName(listings).map((listing) => (
                <div
                  key={listing.id}
                  className={`bg-white rounded-lg shadow p-4 transition-all hover:shadow-md ${
                    selectedListing?.id === listing.id ? 'border-2 border-primary' : 'border border-gray-200'
                  }`}
                >
                  <div 
                    onClick={() => handleListingClick(listing)}
                    className="cursor-pointer"
                  >
                    <h3 className="font-bold text-lg text-gray-800 mb-2">
                      {listing.name}
                    </h3>
                    <p className="text-sm text-gray-600 mb-1">
                      <span className="font-semibold">Type:</span>{' '}
                      {listing.listingType?.title || 'N/A'}
                    </p>
                    <p className="text-sm text-gray-600 mb-1">
                      <span className="font-semibold">Location:</span>{' '}
                      {listing.closestCity}, {listing.province?.title || 'N/A'}
                    </p>
                    {listing.physicalAddress && (
                      <p className="text-sm text-gray-600 mb-2">
                        {listing.physicalAddress}
                      </p>
                    )}
                    {listing.contactNumber && (
                      <p className="text-sm text-gray-700">
                        <span className="font-semibold">Contact:</span>{' '}
                        {listing.contactNumber}
                      </p>
                    )}
                    {listing.description && (
                      <p className="text-sm text-gray-600 mt-2 line-clamp-2">
                        {listing.description}
                      </p>
                    )}
                  </div>
                  <div className="mt-4 pt-3 border-t border-gray-200">
                    <Button
                      onClick={() => {
                        setSelectedListingForProfile(listing);
                        setIsProfileDrawerOpen(true);
                      }}
                      variant="primary"
                      className="w-full"
                    >
                      View Profile
                    </Button>
                  </div>
                </div>
              ))}

              {filterListingsByName(listings).length === 0 && !isLoading && (
                <div className="text-center py-8 text-gray-500">
                  No listings found nearby
                </div>
              )}
            </div>
          )}
          </div>
        </div>

        {/* Right side - Map */}
        <div className="w-2/3 relative bg-gray-200">
        <div 
          ref={mapContainerRef} 
          style={{ width: '100%', height: '100vh' }} 
        />
        
        {/* Map Controls */}
        <div className="absolute top-4 right-4 flex flex-col gap-2">
          {/* Search This Area Button with notification */}
          <div className="relative">
            <button
              onClick={handleSearchThisArea}
              className="bg-white hover:bg-gray-50 text-gray-800 font-semibold p-3 rounded-lg shadow-md transition-colors relative"
              title="Search this area"
            >
              <Search className="w-5 h-5" />
              {hasMapMoved && (
                <span className="absolute -top-1 -right-1 flex h-3 w-3">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-3 w-3 bg-primary"></span>
                </span>
              )}
            </button>
            
            {/* Tooltip notification */}
            {hasMapMoved && (
              <div className="absolute top-0 right-full mr-2 bg-gray-900 text-white text-xs py-2 px-3 rounded-lg shadow-lg whitespace-nowrap animate-pulse">
                New results available
                <div className="absolute top-1/2 -right-1 transform -translate-y-1/2 w-0 h-0 border-t-4 border-t-transparent border-b-4 border-b-transparent border-l-4 border-l-gray-900"></div>
              </div>
            )}
          </div>
          
          {/* Zoom In */}
          <button
            onClick={handleZoomIn}
            className="bg-white hover:bg-gray-50 text-gray-800 font-semibold p-3 rounded-lg shadow-md transition-colors"
            title="Zoom in"
          >
            <ZoomIn className="w-5 h-5" />
          </button>
          
          {/* Zoom Out */}
          <button
            onClick={handleZoomOut}
            className="bg-white hover:bg-gray-50 text-gray-800 font-semibold p-3 rounded-lg shadow-md transition-colors"
            title="Zoom out"
          >
            <ZoomOut className="w-5 h-5" />
          </button>
          
          {/* Go to My Location */}
          <button
            onClick={handleGoToMyLocation}
            className="bg-white hover:bg-gray-50 text-gray-800 font-semibold p-3 rounded-lg shadow-md transition-colors"
            title="Go to my location"
          >
            <Locate className="w-5 h-5" />
          </button>
        </div>
        
        {/* Selected listing details overlay */}
        {selectedListing && (
          <div className="absolute top-4 left-4 right-4 bg-white rounded-lg shadow-lg p-4 max-w-md">
            <button
              onClick={() => setSelectedListing(null)}
              className="absolute top-2 right-2 text-gray-400 hover:text-gray-600"
            >
              ✕
            </button>
            <h2 className="text-xl font-bold text-gray-800 mb-2 pr-6">
              {selectedListing.name}
            </h2>
            <p className="text-sm text-gray-600 mb-2">
              <span className="font-semibold">Type:</span>{' '}
              {selectedListing.listingType?.title || 'N/A'}
            </p>
            <p className="text-sm text-gray-600 mb-2">
              <span className="font-semibold">Location:</span>{' '}
              {selectedListing.closestCity}, {selectedListing.province?.title || 'N/A'}
            </p>
            {selectedListing.physicalAddress && (
              <p className="text-sm text-gray-600 mb-2">
                <span className="font-semibold">Address:</span>{' '}
                {selectedListing.physicalAddress}
              </p>
            )}
            {selectedListing.contactNumber && (
              <p className="text-sm text-gray-700 mb-2">
                <span className="font-semibold">Contact:</span>{' '}
                {selectedListing.contactNumber}
              </p>
            )}
            {selectedListing.email && (
              <p className="text-sm text-gray-700 mb-2">
                <span className="font-semibold">Email:</span>{' '}
                {selectedListing.email}
              </p>
            )}
            {selectedListing.websiteAddress && (
              <p className="text-sm text-gray-700 mb-2">
                <span className="font-semibold">Website:</span>{' '}
                <a
                  href={selectedListing.websiteAddress}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:underline"
                >
                  {selectedListing.websiteAddress}
                </a>
              </p>
            )}
            {selectedListing.description && (
              <p className="text-sm text-gray-600 mt-3">
                {selectedListing.description}
              </p>
            )}
          </div>
        )}
        </div>
      </div>

      {/* Profile Drawer */}
      {selectedListingForProfile && (
        <Drawer
          isOpen={isProfileDrawerOpen}
          onClose={() => {
            setIsProfileDrawerOpen(false);
            setSelectedListingForProfile(null);
          }}
          title={`${selectedListingForProfile.name} - Profile`}
          size="large"
        >
          <ListingForm
            initialValues={selectedListingForProfile}
            onSubmit={() => {}}
            onCancel={() => {
              setIsProfileDrawerOpen(false);
              setSelectedListingForProfile(null);
            }}
            isPublicView={true}
            readonly={true}
          />
        </Drawer>
      )}
    </div>
  );
};

export default ListingSearch;
